/**
 * EnginePhone — the conversation as it read in the engine's own mobile app
 * (docs/features/conversations.md §5.13).
 *
 * One phone per engine look: ChatGPT, Gemini, Le Chat (Mistral), Perplexity, and Google —
 * whose first turn is a search results page with its AI Overview and every later turn the
 * AI Mode chat it continues in. The look is an APPROXIMATION in the app's own fonts (no
 * engine font is shipped); the input bar is drawn greyed on purpose, the owner kept it
 * for recognisability.
 *
 * What is ours, not the engine's, sits on our own neutral chrome: the « Tour N · intention »
 * separators and the drop-out band. Answers are cut at about seven lines; the text, the
 * « Voir la réponse complète » link and the « +N sources » chip open the full answer.
 *
 * Security: engine prose and URLs are attacker-chosen web text. `HighlightedResponse`
 * renders text nodes only; nothing here uses dangerouslySetInnerHTML.
 *
 * Citations are POSITIONAL: the chips are `sources.slice(0, 3)` and their numbers are the
 * index + 1, so they always agree with the inline `[n]` markers.
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ConversationTurn, ConversationAnalysisStatus } from '@traaker/shared';
import { HighlightedResponse } from '../../common/HighlightedResponse';
import { EngineLogo } from '../../brand/EngineLogo';
import { BUCKET_COLOR, isOwnSource, ownSourceHighlights, sourceDomainAt, turnBucketView } from './conversationView';
import { highlightsOf, dropReason } from './turnText';

// ---------------------------------------------------------------------------
// Themes
// ---------------------------------------------------------------------------

const INTER = 'Inter, -apple-system, "Segoe UI", system-ui, sans-serif';
const ROBOTO = 'Roboto, "Google Sans", Arial, system-ui, sans-serif';
const ARIAL = 'Arial, Helvetica, sans-serif';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
/** Seven lines of the 13.5px / 1.6 answer text. */
const CLAMP_PX = Math.round(13.5 * 1.6 * 7);

interface Theme {
  font: string;
  bg: string;
  ink: string;
  accent: string;
  bubble: object;
  chip: { bg: string; fg: string; border?: string; font?: string; radius?: number };
}

const THEMES: Record<string, Theme> = {
  chatgpt: {
    font: INTER, bg: '#FFFFFF', ink: '#0D0D0D', accent: '#0D0D0D',
    bubble: { bgcolor: '#F4F4F4', color: '#0D0D0D', borderRadius: '22px' },
    chip: { bg: '#F4F4F4', fg: '#5D5D5D' },
  },
  gemini: {
    font: ROBOTO, bg: '#FFFFFF', ink: '#1F1F1F', accent: '#0B57D0',
    bubble: { bgcolor: '#E9EEF6', color: '#1F1F1F', borderRadius: '18px 4px 18px 18px' },
    chip: { bg: '#F0F4F9', fg: '#444746' },
  },
  mistral: {
    font: ARIAL, bg: '#FFFFFF', ink: '#1A1A1A', accent: '#FA500F',
    bubble: { bgcolor: '#F2F2F2', color: '#1A1A1A', borderRadius: '12px' },
    chip: { bg: '#FFF1EA', fg: '#A33A0B' },
  },
  perplexity: {
    font: INTER, bg: '#FCFCF9', ink: '#13343B', accent: '#20808D',
    bubble: {},
    chip: { bg: '#F3F3EE', fg: '#13343B', font: MONO, radius: 8 },
  },
  google: {
    font: ROBOTO, bg: '#FFFFFF', ink: '#202124', accent: '#1A73E8',
    bubble: { bgcolor: '#F1F3F4', color: '#202124', borderRadius: '20px' },
    chip: { bg: '#FFFFFF', fg: '#3C4043', border: '1px solid #DADCE0', radius: 10 },
  },
};
const themeOf = (engine: string): Theme => THEMES[engine] ?? THEMES.chatgpt;

/** Gemini / AI Mode sparkle, drawn — it carries no data. */
function Sparkle({ size = 18 }: { size?: number }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: size, height: size, flex: 'none',
        background: 'linear-gradient(135deg, #4285F4, #9B72CB 55%, #D96570)',
        clipPath: 'polygon(50% 0, 61% 39%, 100% 50%, 61% 61%, 50% 100%, 39% 61%, 0 50%, 39% 39%)',
      }}
    />
  );
}

function GoogleWordmark() {
  const letters: [string, string][] = [['G', '#4285F4'], ['o', '#EA4335'], ['o', '#FBBC05'], ['g', '#4285F4'], ['l', '#34A853'], ['e', '#EA4335']];
  return (
    <Box component="span" sx={{ fontFamily: ROBOTO, fontSize: 21, fontWeight: 500, letterSpacing: '-0.5px' }}>
      {letters.map(([c, col], i) => <span key={i} style={{ color: col }}>{c}</span>)}
    </Box>
  );
}

const ICON_SX = { width: 26, height: 26, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 13, flex: 'none' };

function Header({ engine, title }: { engine: string; title: string }) {
  const th = themeOf(engine);
  const row = { display: 'flex', alignItems: 'center', gap: 1, px: 2.25, pt: 0.75, pb: 1.25, borderBottom: '1px solid #F0EEF4', fontFamily: th.font, color: th.ink, bgcolor: th.bg } as const;
  switch (engine) {
    case 'gemini':
      return (
        <Box sx={row}>
          <Box component="span" sx={{ fontSize: 18, color: '#444746' }}>←</Box>
          <Box sx={{ flex: 1, textAlign: 'center', lineHeight: 1.2, minWidth: 0 }}>
            <Box sx={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</Box>
            <Box sx={{ fontSize: 10.5, color: '#5E5E5E' }}>Gemini</Box>
          </Box>
          <Box sx={{ ...ICON_SX, bgcolor: '#1A73E8', color: '#fff', fontSize: 11 }}>{title.slice(0, 1).toUpperCase()}</Box>
        </Box>
      );
    case 'mistral':
      return (
        <Box sx={row}>
          <Box component="span" sx={{ fontSize: 18, color: th.accent }}>☰</Box>
          <Box sx={{ flex: 1, fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</Box>
          <Box component="span" sx={{ color: th.accent }}>⇪</Box>
          <Box sx={{ ...ICON_SX, width: 20, height: 20, color: th.accent, border: `1.5px solid ${th.accent}` }}>+</Box>
        </Box>
      );
    case 'perplexity':
      return (
        <Box sx={{ ...row, justifyContent: 'space-between' }}>
          <Box component="span" sx={{ fontSize: 17 }}>◯</Box>
          <Box component="span" sx={{ color: th.accent, fontSize: 17, fontWeight: 500, letterSpacing: '-0.01em' }}>perplexity</Box>
          <Box component="span" sx={{ fontSize: 14 }}>⊕</Box>
        </Box>
      );
    case 'google':
      return (
        <Box sx={{ ...row, justifyContent: 'space-between' }}>
          <Box component="span" sx={{ fontSize: 18, color: '#5F6368' }}>☰</Box>
          <GoogleWordmark />
          <Box sx={{ ...ICON_SX, bgcolor: '#5F6368', color: '#fff', fontSize: 11 }}>{title.slice(0, 1).toUpperCase()}</Box>
        </Box>
      );
    default:
      return (
        <Box sx={row}>
          <Box component="span" sx={{ fontSize: 18 }}>☰</Box>
          <Box sx={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 15 }}>
            ChatGPT <Box component="span" sx={{ color: '#8E8E8E', fontWeight: 400 }}>›</Box>
          </Box>
          <Box component="span" sx={{ fontSize: 15 }}>✎</Box>
        </Box>
      );
  }
}

function InputBar({ engine, followUp }: { engine: string; followUp: boolean }) {
  const { t } = useTranslation();
  const th = themeOf(engine);
  const pill = { display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1.1, fontSize: 13, color: '#8E8E93', fontFamily: th.font } as const;
  const body = (() => {
    switch (engine) {
      case 'gemini':
        return (
          <Box sx={{ ...pill, bgcolor: '#fff', border: '1px solid #C4C7C5', borderRadius: '28px', py: 0.5, pr: 0.5, pl: 1.75 }}>
            <Box sx={{ flex: 1 }}>{t('conversations.view.inputGemini')}</Box>
            <Box sx={{ display: 'flex', gap: 1.75, bgcolor: '#D3E3FD', borderRadius: '22px', px: 1.75, py: 1, color: '#0B57D0' }}>
              <span>🎙</span><span>📷</span>
            </Box>
          </Box>
        );
      case 'mistral':
        return (
          <Box sx={{ ...pill, border: '1px solid #DDD', borderRadius: '8px' }}>
            <span>+</span><Box sx={{ flex: 1 }}>{t('conversations.view.inputMistral')}</Box>
            <Box sx={{ ...ICON_SX, borderRadius: '6px', bgcolor: '#E8E8E8', color: '#999' }}>➜</Box>
          </Box>
        );
      case 'perplexity':
        return (
          <Box sx={{ ...pill, bgcolor: '#ECECE6', borderRadius: '28px', color: '#64645F' }}>
            <Box sx={{ flex: 1 }}>{t('conversations.view.inputPerplexity')}</Box><span>🎙</span>
          </Box>
        );
      case 'google':
        return (
          <Box sx={{ ...pill, bgcolor: '#F1F3F4', borderRadius: '28px' }}>
            <Box sx={{ flex: 1 }}>{followUp ? t('conversations.view.inputGoogleFollowUp') : t('conversations.view.inputGoogle')}</Box>
            <span style={{ color: '#4285F4' }}>🎙</span><span style={{ color: '#EA4335' }}>⌾</span>
          </Box>
        );
      default:
        return (
          <Box sx={{ ...pill, border: '1px solid #E5E5E5', borderRadius: '24px', boxShadow: '0 2px 8px rgba(0,0,0,.06)' }}>
            <span>+</span><Box sx={{ flex: 1 }}>{t('conversations.view.inputChatgpt')}</Box><span>🎙</span>
            <Box sx={{ ...ICON_SX, bgcolor: '#0D0D0D', color: '#fff', fontSize: 10 }}>▮▮</Box>
          </Box>
        );
    }
  })();
  // Greyed: it is drawn for recognisability, it cannot be typed into.
  return <Box aria-hidden sx={{ flex: 'none', px: 1.5, pt: 1, pb: 2.25, opacity: 0.85, pointerEvents: 'none', bgcolor: th.bg }}>{body}</Box>;
}

// ---------------------------------------------------------------------------
// One turn
// ---------------------------------------------------------------------------

function SourceChips({ turn, engine, onOpen }: { turn: ConversationTurn; engine: string; onOpen: () => void }) {
  const { t } = useTranslation();
  const th = themeOf(engine);
  const n = (turn.sourceLinks?.length || turn.sources?.length) ?? 0;
  if (n === 0) return null;
  const chip = (own: boolean) => ({
    display: 'inline-flex', alignItems: 'center', gap: 0.6, fontSize: 11, px: 1, py: 0.4,
    borderRadius: `${th.chip.radius ?? 999}px`, fontFamily: th.chip.font ?? th.font,
    bgcolor: own ? '#EDE5FF' : th.chip.bg, color: own ? 'primary.dark' : th.chip.fg,
    border: own ? 'none' : th.chip.border ?? 'none', fontWeight: own ? 600 : 400,
    cursor: 'pointer', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  });
  // Positional: index i is source [i + 1], whatever is shown.
  const all = Array.from({ length: n }, (_, i) => ({ i, domain: sourceDomainAt(turn, i), own: isOwnSource(turn, i) }));
  const shown = all.slice(0, 3);
  const hidden = all.slice(3);
  const hiddenOwn = hidden.find((s) => s.own);
  return (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
      {shown.map((s) => (
        <Box key={s.i} component="button" type="button" onClick={onOpen} sx={{ all: 'unset', ...chip(s.own) }}>
          {`${s.i + 1} · ${s.domain ?? t('conversations.view.sourceUnknown')}`}
        </Box>
      ))}
      {hidden.length > 0 && (
        <Box component="button" type="button" onClick={onOpen} sx={{ all: 'unset', ...chip(!!hiddenOwn), fontWeight: 600 }}>
          {hiddenOwn
            ? t('conversations.view.moreSourcesOwn', { count: hidden.length, domain: hiddenOwn.domain ?? '' })
            : t('conversations.view.moreSources', { count: hidden.length })}
        </Box>
      )}
    </Box>
  );
}

/** The answer cut at ~7 lines, with a fade in the screen's own colour. */
function ClampedAnswer({ turn, brandId, bg, onOpen }: { turn: ConversationTurn; brandId?: string; bg: string; onOpen: () => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(true);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setOverflows(el.scrollHeight > el.clientHeight + 2);
  }, [turn.engineResponse]);
  const highlights = highlightsOf(turn.targetMatches);
  return (
    <>
      <Box
        ref={ref}
        onClick={(e) => { if (!(e.target as HTMLElement).closest('a')) onOpen(); }}
        sx={{
          maxHeight: CLAMP_PX, overflow: 'hidden', position: 'relative', cursor: 'pointer',
          '&::after': overflows ? { content: '""', position: 'absolute', left: 0, right: 0, bottom: 0, height: '3.2em', background: `linear-gradient(transparent, ${bg})` } : {},
        }}
      >
        <HighlightedResponse
          text={turn.engineResponse ?? ''}
          brandId={brandId}
          sources={turn.sources}
          sourceLinks={turn.sourceLinks}
          targetHighlights={highlights.prose}
          targetSourceHighlights={ownSourceHighlights(turn, highlights.sources)}
        />
      </Box>
      {overflows && (
        <Box component="button" type="button" onClick={onOpen} sx={{ all: 'unset', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: 'var(--phone-accent)', '&:hover': { textDecoration: 'underline' } }}>
          {t('conversations.view.seeFullAnswer')}
        </Box>
      )}
    </>
  );
}

function TurnSeparator({ turn, cellAnalysisStatus }: { turn: ConversationTurn; cellAnalysisStatus?: ConversationAnalysisStatus }) {
  const { t } = useTranslation();
  const b = turnBucketView(turn, cellAnalysisStatus);
  const label = b.kind === 'bucket'
    ? t(`conversations.view.bucket_${b.bucket}`)
    : b.kind === 'pending' ? t('conversations.view.intentPending') : t('conversations.view.intentUnanalyzed');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2, fontFamily: INTER, '&::before, &::after': { content: '""', flex: 1, height: '1px', bgcolor: '#EEEBF3' } }}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1, py: 0.25, borderRadius: 999, bgcolor: '#F5F3FA', color: '#4B4757', fontSize: 10.5, fontWeight: 600 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: b.kind === 'bucket' ? BUCKET_COLOR[b.bucket] : '#9CA3AF' }} />
        {t('conversations.view.turnSeparator', { n: turn.turn, intent: label })}
      </Box>
    </Box>
  );
}

function AnswerHead({ engine, googleLabel }: { engine: string; googleLabel?: string }) {
  const { t } = useTranslation();
  if (engine === 'gemini') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, mb: 0.5, color: '#444746' }}>
        <EngineLogo engine="gemini" size={20} /><span aria-hidden>🔈</span>
      </Box>
    );
  }
  if (engine === 'mistral') return <Box sx={{ mt: 1.5, mb: 0.5 }}><EngineLogo engine="mistral" size={20} /></Box>;
  if (engine === 'perplexity') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5, mb: 0.5, fontWeight: 500, fontSize: 13, color: '#13343B' }}>
        <EngineLogo engine="perplexity" size={18} /> {t('conversations.view.perplexityAnswer')}
      </Box>
    );
  }
  if (engine === 'google') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5, mb: 0.25, fontWeight: 500, fontSize: 12.5, color: '#202124' }}>
        <Sparkle size={15} /> {googleLabel}
      </Box>
    );
  }
  return null;
}

function GeminiActions() {
  return (
    <Box aria-hidden sx={{ display: 'flex', gap: 2.25, color: '#444746', fontSize: 13, mt: 1 }}>
      <span>👍︎</span><span>👎︎</span><span style={{ fontWeight: 700, color: '#4285F4' }}>G</span><span>⇪</span><span>⧉</span><span>⋮</span>
    </Box>
  );
}

function GoogleOverview({ turn, brandId, onOpen }: { turn: ConversationTurn; brandId?: string; onOpen: () => void }) {
  const { t } = useTranslation();
  const n = (turn.sourceLinks?.length || turn.sources?.length) ?? 0;
  const all = Array.from({ length: n }, (_, i) => ({ i, domain: sourceDomainAt(turn, i), own: isOwnSource(turn, i) }));
  const tabs = ['All', 'Videos', 'Images', 'Shopping', 'Web', 'News'] as const;
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #DADCE0', borderRadius: '24px', px: 1.5, py: 1, fontSize: 13, color: '#202124', boxShadow: '0 1px 3px rgba(60,64,67,.15)' }}>
        <span aria-hidden>🔍</span>
        <Box sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{turn.personaMessage}</Box>
        <span aria-hidden style={{ color: '#4285F4' }}>🎙</span>
      </Box>
      <Box aria-hidden sx={{ display: 'flex', gap: 2, fontSize: 12, color: '#5F6368', mt: 1.25, mx: 0.25, borderBottom: '1px solid #ECEDEF', whiteSpace: 'nowrap', overflow: 'hidden' }}>
        {tabs.map((k, i) => (
          <Box key={k} component="span" sx={{ pb: 0.9, ...(i === 0 ? { color: '#202124', fontWeight: 500, borderBottom: '2px solid #202124' } : {}) }}>
            {t(`conversations.view.googleTab${k}`)}
          </Box>
        ))}
      </Box>
      <Box sx={{ bgcolor: '#F8FAFD', mx: -1.75, px: 1.75, pt: 0.5, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, mb: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 500, fontSize: 12.5, color: '#202124' }}>
            <Sparkle size={15} /> {t('conversations.view.googleOverview')}
          </Box>
          {n > 0 && (
            <Box sx={{ fontSize: 11, color: '#5F6368' }}>
              {n > 3 ? t('conversations.view.googleMoreSources', { count: n }) : t('conversations.view.googleSources', { count: n })}
            </Box>
          )}
        </Box>
        <ClampedAnswer turn={turn} brandId={brandId} bg="#F8FAFD" onOpen={onOpen} />
        {n > 0 && (
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', mt: 1.25, scrollbarWidth: 'none' }}>
            {all.map((s) => (
              <Box
                key={s.i}
                component="button"
                type="button"
                onClick={onOpen}
                sx={{ all: 'unset', cursor: 'pointer', flex: 'none', width: 150, bgcolor: s.own ? '#EDE5FF' : '#E9EEF6', borderRadius: '12px', px: 1.25, py: 1, fontSize: 11.5, color: '#202124', lineHeight: 1.35 }}
              >
                <Box sx={{ color: s.own ? 'primary.dark' : '#5F6368', fontSize: 10.5, mb: 0.25 }}>{`${s.i + 1} · ${s.domain ?? ''}`}</Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
}

// ---------------------------------------------------------------------------
// Phone
// ---------------------------------------------------------------------------

export interface EnginePhoneProps {
  engine: string;
  turns: ConversationTurn[];
  brandId?: string;
  /** The conversation's name — the chat title in the apps that show one. */
  title: string;
  /** « 9:41 » in the status bar: the launch's local time. */
  clock: string;
  cellAnalysisStatus?: ConversationAnalysisStatus;
  dropOutTurn: number | null;
  /** Bumped by the page to scroll a turn into view (‹ ›, the intent list). */
  scrollTo: { turn: number; nonce: number } | null;
  onActiveTurn: (turn: number) => void;
  onOpenResponse: (turn: number) => void;
  /** Rendered over the screen while the transcript loads or fails. */
  overlay?: ReactNode;
}

export function EnginePhone({
  engine, turns, brandId, title, clock, cellAnalysisStatus, dropOutTurn, scrollTo, onActiveTurn, onOpenResponse, overlay,
}: EnginePhoneProps) {
  const { t } = useTranslation();
  const th = themeOf(engine);
  const chatRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef(1);

  // A new transcript starts at its top.
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = 0;
    activeRef.current = 1;
  }, [turns]);

  useEffect(() => {
    if (!scrollTo || !chatRef.current) return;
    const node = chatRef.current.querySelector<HTMLElement>(`[data-turn="${scrollTo.turn}"]`);
    if (node) chatRef.current.scrollTop = node.offsetTop - 8;
  }, [scrollTo]);

  // Scroll-spy: the active turn is the last one whose separator passed the top third.
  const onScroll = () => {
    const chat = chatRef.current;
    if (!chat) return;
    let cur = turns[0]?.turn ?? 1;
    chat.querySelectorAll<HTMLElement>('[data-turn]').forEach((el) => {
      if (el.offsetTop - chat.scrollTop < chat.clientHeight / 3) cur = Number(el.dataset.turn);
    });
    if (cur !== activeRef.current) { activeRef.current = cur; onActiveTurn(cur); }
  };

  const prevOf = new Map(turns.map((x, i) => [x.turn, turns[i - 1]]));
  const answerSx = {
    color: th.ink,
    '& .MuiTypography-root': { fontFamily: th.font, color: th.ink, letterSpacing: 0 },
    '& .MuiTypography-body2, & .MuiTypography-body1': { fontSize: '13.5px', lineHeight: 1.6, mb: '8px' },
    '& ol, & ul': { pl: '1.2em', my: '6px' },
    '& li': { mb: '3px' },
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          width: '100%', maxWidth: 410, height: 820, borderRadius: '54px', bgcolor: '#17151D', p: '13px',
          boxShadow: '0 30px 60px -20px rgba(23,21,29,.35)', position: 'relative',
          '--phone-accent': th.accent,
        } as object}
      >
        <Box sx={{ width: '100%', height: '100%', borderRadius: '42px', bgcolor: th.bg, overflow: 'hidden', display: 'flex', flexDirection: 'column', fontFamily: th.font, position: 'relative' }}>
          {/* status bar */}
          <Box aria-hidden sx={{ height: 46, flex: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: '28px', pl: '32px', fontSize: 13, fontWeight: 600, position: 'relative', fontFamily: INTER, color: '#111' }}>
            <span>{clock}</span>
            <Box sx={{ position: 'absolute', left: '50%', top: 10, transform: 'translateX(-50%)', width: 110, height: 28, borderRadius: 20, bgcolor: '#17151D' }} />
            <span>▂▄▆ ◔</span>
          </Box>
          <Header engine={engine} title={title} />

          <Box ref={chatRef} onScroll={onScroll} sx={{ flex: 1, overflowY: 'auto', px: 1.75, pt: 1, pb: 3, position: 'relative' }}>
            {turns.map((turn, i) => {
              const prev = prevOf.get(turn.turn);
              const isDrop = dropOutTurn === turn.turn;
              const open = () => onOpenResponse(turn.turn);
              const question = engine === 'perplexity'
                ? <Typography sx={{ fontFamily: th.font, fontSize: 19, lineHeight: 1.3, fontWeight: 500, color: th.ink }}>{turn.personaMessage}</Typography>
                : (
                  <Box sx={{ ml: 'auto', maxWidth: '82%', width: 'fit-content', px: 1.6, py: 1.1, fontSize: 13.5, lineHeight: 1.45, ...th.bubble }}>
                    {turn.personaMessage}
                  </Box>
                );
              return (
                <Box key={turn.turn}>
                  {isDrop && (
                    <Box sx={{ mx: -1.75, mt: 2, mb: 0.5, px: 2, py: 1, bgcolor: '#FEF3C7', borderTop: '1px solid #FCD34D', borderBottom: '1px solid #FCD34D', fontSize: 12, color: '#78350F', lineHeight: 1.4, fontFamily: INTER }}>
                      <Box sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {t('conversations.view.dropBand', { n: turn.turn })}
                      </Box>
                      {dropReason(turn, prev, t)}
                    </Box>
                  )}
                  {engine === 'google' && i === 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.25, mb: 0.5 }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 11.5, fontWeight: 500, color: '#3C4043', bgcolor: '#F1F3F4', borderRadius: 999, px: 1.4, py: 0.5 }}>
                        <Sparkle size={13} /> {t('conversations.view.googleSwitch')}
                      </Box>
                    </Box>
                  )}
                  <Box data-turn={turn.turn}>
                    <TurnSeparator turn={turn} cellAnalysisStatus={cellAnalysisStatus} />
                    {engine === 'google' && i === 0 ? (
                      <Box sx={answerSx}><GoogleOverview turn={turn} brandId={brandId} onOpen={open} /></Box>
                    ) : engine === 'perplexity' ? (
                      <>
                        {question}
                        <Box sx={{ fontWeight: 500, fontSize: 13, color: th.ink, mt: 1.5 }}>☰ {t('conversations.view.perplexitySources')}</Box>
                        <SourceChips turn={turn} engine={engine} onOpen={open} />
                        <AnswerHead engine={engine} />
                        <Box sx={answerSx}><ClampedAnswer turn={turn} brandId={brandId} bg={th.bg} onOpen={open} /></Box>
                      </>
                    ) : (
                      <>
                        {question}
                        <AnswerHead engine={engine} googleLabel={t('conversations.view.googleMode')} />
                        <Box sx={{ ...answerSx, mt: engine === 'chatgpt' ? 1.5 : 0 }}>
                          <ClampedAnswer turn={turn} brandId={brandId} bg={th.bg} onOpen={open} />
                        </Box>
                        <SourceChips turn={turn} engine={engine} onOpen={open} />
                        {engine === 'gemini' && <GeminiActions />}
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <InputBar engine={engine} followUp={turns.length > 1} />
          {overlay && (
            <Box sx={{ position: 'absolute', inset: '46px 0 0 0', bgcolor: th.bg, display: 'grid', placeItems: 'center', p: 3, textAlign: 'center' }}>
              {overlay}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
